import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { ReportTheme } from '../../types';
import { getChartColor } from './chartUtils';
import { validateDateKeyedData, filterDataByDateRange } from './functionalWidgets';

interface DevicesBrowsersPieChartWidgetProps {
  data: any;
  data_points?: string[];
  report_start_date?: string;
  report_end_date?: string;
  reportTheme: ReportTheme;
}

export const DevicesBrowsersPieChartWidget: React.FC<DevicesBrowsersPieChartWidgetProps> = ({ 
  data, 
  data_points,
  report_start_date,
  report_end_date,
  reportTheme 
}) => {
  console.log('DevicesBrowsersPieChartWidget raw data:', data);
  console.log('DevicesBrowsersPieChartWidget data_points:', data_points);
  console.log('DevicesBrowsersPieChartWidget report_start_date:', report_start_date);
  console.log('DevicesBrowsersPieChartWidget report_end_date:', report_end_date);

  // Process the raw API data internally for Devices and Browsers
  const processData = () => {
    // Validate data structure and get entries
    const validation = validateDateKeyedData(data);
    if (!validation.isValid) {
      console.warn(`DevicesBrowsersPieChartWidget: ${validation.error}`);
      return null;
    }

    // Filter data by date range
    const filteredEntries = filterDataByDateRange(validation.entries, report_start_date, report_end_date);
    
    if (filteredEntries.length === 0) {
      console.warn('DevicesBrowsersPieChartWidget: No data entries found in date range');
      return null;
    }

    // Aggregate data across all dates
    const deviceTotals: { [key: string]: number } = {};
    const browserTotals: { [key: string]: number } = {};

    filteredEntries.forEach(([dateKey, dayData]: [string, any]) => {
      if (!dayData || typeof dayData !== 'object') {
        return;
      }

      // Process device data
      if (dayData.devices && typeof dayData.devices === 'object') {
        Object.entries(dayData.devices).forEach(([deviceName, deviceData]: [string, any]) => {
          if (deviceName !== '(not set)' && deviceData && typeof deviceData === 'object') {
            const sessions = deviceData.sessions || deviceData.count || 0;
            deviceTotals[deviceName] = (deviceTotals[deviceName] || 0) + sessions;
          }
        });
      }

      // Process browser data
      if (dayData.browsers && typeof dayData.browsers === 'object') {
        Object.entries(dayData.browsers).forEach(([browserName, browserData]: [string, any]) => {
          if (browserName !== '(not set)' && browserData && typeof browserData === 'object') {
            const sessions = browserData.sessions || browserData.count || 0;
            browserTotals[browserName] = (browserTotals[browserName] || 0) + sessions;
          }
        });
      }
    });

    // Create pie chart data for devices
    const devicePieData = Object.entries(deviceTotals)
      .map(([deviceName, sessions], index) => ({
        name: deviceName,
        value: sessions,
        color: getChartColor(index, reportTheme.theme)
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 6); // Top 6 devices

    // Create pie chart data for browsers
    const browserPieData = Object.entries(browserTotals)
      .map(([browserName, sessions], index) => ({
        name: browserName,
        value: sessions,
        color: getChartColor(index, reportTheme.theme)
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 6); // Top 6 browsers

    return {
      devices: devicePieData.length > 0 ? devicePieData : null,
      browsers: browserPieData.length > 0 ? browserPieData : null
    };
  };

  const processedData = processData();
  console.log('DevicesBrowsersPieChartWidget processed data:', processedData);

  // Don't render if no data
  if (!processedData || (!processedData.devices && !processedData.browsers)) {
    return null;
  }

  const renderPieChart = (data: any[], title: string) => {
    if (!data || data.length === 0) return null;

    return (
      <div className="h-1/2 w-full flex flex-col">
        <h4 className={`text-sm font-medium text-center mb-2 ${
          reportTheme.theme === 'light' ? 'text-gray-900' : 'text-foreground'
        }`}>{title}</h4>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              outerRadius={60}
              fill="#8884d8"
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip 
              formatter={(value: any, name: string) => [value, 'Sessions']}
              labelStyle={{
                color: reportTheme.theme === 'light' ? '#374151' : '#f3f4f6'
              }}
              contentStyle={{
                backgroundColor: reportTheme.theme === 'light' ? '#ffffff' : '#1f2937',
                border: `1px solid ${reportTheme.theme === 'light' ? '#e5e7eb' : '#374151'}`,
                borderRadius: '6px'
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  };

  return (
    <div className="h-full w-full flex flex-col">
      {processedData.devices && renderPieChart(processedData.devices, "Devices")}
      {processedData.browsers && renderPieChart(processedData.browsers, "Browsers")}
    </div>
  );
};
